import {
  IonBadge,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSearchbar,
  // T-077: IonSegment/IonSegmentButton non più importati — segmentato rimosso.
  IonTitle,
  IonToolbar,
  useIonViewWillEnter,
} from '@ionic/react';
import { cameraOutline, refreshOutline } from 'ionicons/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getSupabase } from '../../lib/supabase.js';
import { useAiSuggestions } from '../../lib/haccp/suggestions.js';
import { AiSuggestionsFab } from '../../components/AiSuggestionsFab.js';
import { FeatureIntroBlock, isIntroDismissed } from '../../components/FeatureCard.js';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useHistory } from 'react-router-dom';
import { DocumentsProcessingSection } from '../../components/DocumentsProcessingSection.js';
import { isActiveDocumentListRow } from '../../lib/documents/documentListVisibility.js';
import { AiRobotIcon } from '../../components/AiRobotIcon.js';
import { useNetworkOnline } from '../../lib/hooks/useNetworkOnline.js';

type DocKind = 'all' | 'fattura' | 'ddt';

// Tipo unificato che astrae fiscal_archive.documents (fatture/note di credito) e
// public.documents (DDT). source determina quale dettaglio aprire.
interface UnifiedDoc {
  id: string;
  source: 'fiscal' | 'public';
  type: 'fattura' | 'ddt' | 'nota_credito' | 'nota_debito' | 'autofattura';
  number: string;
  date: string;
  supplier_id: string | null;
  total_amount: number | null;
  status: string;
  automaticImport: boolean;
}

interface SupplierMap {
  [id: string]: string;
}

async function fetchDocs(kind: DocKind, search: string, tenantId: string | null): Promise<{ docs: UnifiedDoc[]; suppliers: SupplierMap }> {
  const supabase = getSupabase();
  const all: UnifiedDoc[] = [];

  // Senza tenant attivo non possiamo filtrare → ritorna vuoto (post-fix RLS,
  // il DB negherebbe comunque, ma evitiamo round-trip).
  if (!tenantId) return { docs: [], suppliers: {} };

  // 1. fiscal_archive.documents (fatture, note credito, ecc.)
  // Filtro esplicito tenant_id come defense-in-depth: la migration RLS
  // 20260527140000_active_tenant_scope già lo enforza server-side, ma il filtro
  // client riduce traffico e rende il comportamento esplicito nel codice.
  if (kind === 'all' || kind === 'fattura') {
    let q1 = supabase
      .from('fiscal_documents')
      .select('id, type, number, date, supplier_id, total_amount, status')
      .eq('tenant_id', tenantId)
      .neq('status', 'rejected')
      .order('date', { ascending: false })
      .limit(100);
    if (kind === 'fattura') q1 = q1.eq('type', 'fattura');
    const { data, error } = await q1;
    if (error) throw error;
    for (const d of (data ?? []) as Array<{ id: string; type: UnifiedDoc['type']; number: string; date: string; supplier_id: string | null; total_amount: number | null; status: string }>) {
      if (!isActiveDocumentListRow({
        source: 'fiscal',
        status: d.status,
      })) continue;
      all.push({ ...d, source: 'fiscal', automaticImport: false });
    }
  }

  // 2. public.documents (DDT)
  if (kind === 'all' || kind === 'ddt') {
    const { data, error } = await supabase
      .from('documents')
      .select('id, kind, document_number, document_date, supplier_id, created_at, archived_at, ocr_parsed')
      .eq('tenant_id', tenantId)
      .is('archived_at', null)
      .order('document_date', { ascending: false, nullsFirst: false })
      .limit(100);
    if (error) throw error;
    for (const d of (data ?? []) as Array<{ id: string; kind: string; document_number: string | null; document_date: string | null; supplier_id: string | null; created_at: string; archived_at: string | null; ocr_parsed: unknown }>) {
      if (!isActiveDocumentListRow({
        source: 'public',
        archivedAt: d.archived_at,
      })) continue;
      all.push({
        id: d.id,
        source: 'public',
        type: 'ddt',
        number: d.document_number ?? '—',
        date: d.document_date ?? d.created_at.slice(0, 10),
        supplier_id: d.supplier_id,
        total_amount: null,
        status: 'received',
        automaticImport: (() => {
          if (!d.ocr_parsed || typeof d.ocr_parsed !== 'object') return false;
          const provenance = (d.ocr_parsed as { import_provenance?: unknown }).import_provenance;
          return !!provenance
            && typeof provenance === 'object'
            && (provenance as { mode?: unknown; attested?: unknown }).mode === 'automatic_ai'
            && (provenance as { mode?: unknown; attested?: unknown }).attested === true;
        })(),
      });
    }
  }

  // Ordina merge per data desc.
  all.sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));

  // Resolve fornitori in batch (anche qui filtro tenant esplicito).
  const ids = [...new Set(all.map((d) => d.supplier_id).filter((x): x is string => !!x))];
  const suppliers: SupplierMap = {};
  if (ids.length > 0) {
    const { data: srows, error } = await supabase.from('suppliers').select('id,name').eq('tenant_id', tenantId).in('id', ids);
    if (error) throw error;
    for (const r of (srows ?? []) as { id: string; name: string }[]) suppliers[r.id] = r.name;
  }
  const filtered = search.trim()
    ? all.filter((d) =>
        (suppliers[d.supplier_id ?? ''] ?? '').toLowerCase().includes(search.trim().toLowerCase())
        || d.number.toLowerCase().includes(search.trim().toLowerCase()),
      )
    : all;
  return { docs: filtered, suppliers };
}

export function DocumentsPage(): JSX.Element {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const history = useHistory();
  const online = useNetworkOnline();
  const [kind] = useState<DocKind>('all');
  const [search, setSearch] = useState('');

  const tenantId = profile?.active_tenant_id ?? null;
  const { data, isLoading, isFetching, isPaused, isError, isSuccess, refetch } = useQuery({
    queryKey: ['fiscal_archive', tenantId, kind, search],
    queryFn: () => fetchDocs(kind, search, tenantId),
    enabled: !!tenantId && online,
  });
  function refreshDocs(): void {
    if (tenantId && navigator.onLine) void refetch();
  }
  // T-204: Ionic mantiene la pagina montata → tornando dal flusso di import il
  // componente non si rimonta e la lista restava stantia finché non si
  // chiudeva/riapriva l'app. Rifacciamo la fetch a ogni ingresso nella vista,
  // così un documento appena importato compare subito. (In più, CapturePhotoPage
  // invalida ['fiscal_archive'] al commit per gli import sincroni.)
  useIonViewWillEnter(refreshDocs, [tenantId, online, refetch]);
  const suggestions = useAiSuggestions();

  // T-134: intestazione informativa col periodo (mese+anno) del documento più
  // recente già presente nei dati locali. La lista è già ordinata per data
  // desc lato fetchDocs, quindi `docs[0]` è il documento più recente. Statica,
  // non cliccabile, derivata offline (nessuna nuova chiamata). Se la lista è
  // vuota non si mostra. `latestDate` può essere una stringa ISO `YYYY-MM-DD`.
  const docs = data?.docs ?? [];
  const latestDateStr = docs.length > 0 ? docs[0]?.date : undefined;
  const latestPeriod = ((): string | null => {
    if (!latestDateStr) return null;
    const parsed = new Date(latestDateStr);
    if (Number.isNaN(parsed.getTime())) return null;
    // Mese in italiano (es. "maggio 2026"); capitalizzo l'iniziale del mese.
    const formatted = parsed.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  })();

  const docsEmpty = isSuccess && docs.length === 0 && !search;
  const showIntro = docsEmpty
    && !isIntroDismissed('documents_list', profile?.id ?? null, profile?.active_business_id ?? null);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t('documents.title')}</IonTitle>
          <IonButtons slot="end">
            <IonButton
              onClick={refreshDocs}
              disabled={!online || isFetching}
              aria-label={online && isFetching ? 'Aggiornamento documenti in corso' : 'Aggiorna documenti'}
            >
              <IonIcon slot="start" icon={refreshOutline} />
              {isError ? 'Riprova' : 'Aggiorna'}
            </IonButton>
            <IonButton routerLink="/documents/capture" aria-label={t('documents.capture')}>
              <IonIcon slot="icon-only" icon={cameraOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
        {/* T-077: rimosso filtro segmentato DDT/Fatture — cassetto unico,
            badge tipo ancora visibile sulla riga di ogni documento. Lo
            `kind` resta a 'all' (default state, vedi `useState`). */}
        <IonToolbar>
          <IonSearchbar
            placeholder={t('common.search')}
            value={search}
            onIonInput={(ev) => setSearch(String(ev.detail.value ?? ''))}
          />
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {(!online || isPaused || isError) && (
          <IonItem lines="none">
            <IonLabel className="ion-text-wrap" color="medium" role={isError && online ? 'alert' : 'status'}>
              <p>{!online || isPaused
                ? 'Sei offline. Per consultare e aggiornare l’archivio DDT e fatture serve una connessione.'
                : 'Impossibile aggiornare i documenti. Riprova quando la connessione è disponibile.'}</p>
              {data && <p>Mostro gli ultimi documenti caricati.</p>}
            </IonLabel>
          </IonItem>
        )}
        {showIntro && (
          <div style={{ padding: '12px 16px' }}>
            <FeatureIntroBlock
              featureKey="documents_list"
              icon="📄"
              title="Archivio documenti di carico"
              description={
                <>
                  Tutti i tuoi <b>DDT e fatture di acquisto</b> in un unico
                  archivio digitale, organizzato per fornitore e data.
                  Quando carichi un documento con foto o PDF, l'AI estrae
                  automaticamente fornitore, righe, lotti e scadenze: tu
                  rivedi e confermi.
                  <br /><br />
                  L'archivio fiscale è obbligatorio per legge (Reg. CE
                  178/2002 e DPR 633/72), e qui resta consultabile per{' '}
                  <b>10 anni</b>. Dalla scheda di ogni documento puoi
                  rivedere la scansione originale, le righe estratte e
                  collegare le giacenze in magazzino.
                </>
              }
              whatsappQuestion="Ciao, ho dubbi su come caricare e gestire i documenti in TrackEAT, mi puoi aiutare?"
              startLabel="Carica il primo documento"
              onStart={() => history.push('/documents/capture')}
              userId={profile?.id ?? null}
              businessId={profile?.active_business_id ?? null}
              flush
            />
          </div>
        )}
        {/* T-134: intestazione periodo, mostrata solo a lista non vuota.
            Puramente informativa (non cliccabile) in v1. */}
        {!isLoading && latestPeriod && (
          <div
            style={{
              padding: '14px 16px 6px',
              fontSize: '0.95rem',
              fontWeight: 600,
              color: 'var(--ion-color-medium-shade)',
            }}
          >
            {t('documents.periodHeader', {
              period: latestPeriod,
              defaultValue: 'Documenti · {{period}}',
            })}
          </div>
        )}
        {/* T-170 1d: sezione "In elaborazione" (job import async). Self-hide quando
            non ci sono job/draft (flag OFF → nessun job → nulla mostrato). */}
        <DocumentsProcessingSection tenantId={tenantId} businessId={profile?.active_business_id ?? null} />
        {/* T-147: l'accesso alla ricerca di tracciabilità (ex T-138, era qui come
            bottone) è stato spostato come card nella dashboard (TodayPage). I
            documenti restano solo il cassetto DDT/fatture. */}
        <IonList>
          {isLoading && online && !isPaused && (
            <IonItem>
              <IonLabel color="medium">{t('common.loading')}</IonLabel>
            </IonItem>
          )}
          {isSuccess && docs.length === 0 && !showIntro && (
            <IonItem>
              <IonLabel color="medium">{t('documents.empty')}</IonLabel>
            </IonItem>
          )}
          {(data?.docs ?? []).map((doc) => (
            <IonItem
              key={`${doc.source}:${doc.id}`}
              button
              detail
              routerLink={`/documents/${doc.source}/${doc.id}`}
            >
              <IonLabel>
                <h3>{(doc.supplier_id && data?.suppliers[doc.supplier_id]) || '— Fornitore non riconosciuto —'}</h3>
                <p>
                  {doc.type.toUpperCase()} {doc.number} · {new Date(doc.date).toLocaleDateString('it-IT')}
                  {doc.total_amount != null ? ` · €${doc.total_amount.toFixed(2)}` : ''}
                </p>
                {doc.automaticImport && (
                  <p style={{ color: '#4f46e5', fontWeight: 650, marginTop: 4 }}>
                    Importato automaticamente da TrackEAT AI
                  </p>
                )}
              </IonLabel>
              <div slot="end" style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                {doc.automaticImport && (
                  <span
                    aria-label="Importato automaticamente da TrackEAT AI"
                    title="Importato automaticamente da TrackEAT AI"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '5px 9px', borderRadius: 999, color: '#fff',
                      fontSize: 11, fontWeight: 750, letterSpacing: '.02em',
                      background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                      boxShadow: '0 5px 14px rgba(79,70,229,.22)',
                    }}
                  >
                    <AiRobotIcon size={16} />
                    AI Auto
                  </span>
                )}
                <IonBadge color={doc.type === 'fattura' ? 'primary' : 'secondary'}>
                  {doc.type}
                </IonBadge>
              </div>
            </IonItem>
          ))}
        </IonList>
        <AiSuggestionsFab suggestions={suggestions} />
      </IonContent>
    </IonPage>
  );
}
